/**
 * CRM outbound: the screen for the orchestrator that reads a customer's CRM on a timer and
 * calls the people in it.
 *
 * ## Why this exists
 *
 * It did not, for a while. The engine was built, tested to 800-odd tests, and had five HTTP
 * routes — and no page, so from the browser it was indistinguishable from never having been
 * built. Arun: *"Bro where is the UI fir the agarwaals API - how do we have a mechanism to get
 * calls on an interval we specify, dedupe where needed, store data properly (numbers etc) and
 * do those outbound calls. what happened to the crm orchestrator you worked on. dont see in
 * local 5273."*
 *
 * Those four clauses are the page. In order, top to bottom:
 *
 *  1. **on an interval we specify** — the schedule, spelled out in words rather than as a
 *     config dump, with the two switches that decide whether the timer runs at all.
 *  2. **dedupe where needed** — the one-call-per-person-per-day constraint, and the
 *     `re-seen` count, which is the only visible proof it is working. A unique constraint
 *     doing its job leaves no trace by definition, so the screen counts the rows the CRM
 *     handed back that were *not* re-stored.
 *  3. **store data properly (numbers etc)** — the rows, with real numbers and the mapped
 *     conversation fields, because a masked list cannot answer "did the number survive
 *     normalisation" and that is the entire question.
 *  4. **do those outbound calls** — per-row dial state (call id, attempts, why not), and the
 *     run history that produced it.
 *
 * ## One feed, under its account
 *
 * This screen is one feed. Arun: *"the crm outbound is also per customer account and each
 * customer account can have more than one of these can you design it that way please."* So a
 * feed lives at `/accounts/:accountId/crm/:customer`, and `crm-feeds-page.tsx` is the list it
 * hangs off. An account with one feed skips the list; an account with four needs it.
 *
 * Still a separate screen from the callback queue, per M3: *"please keep this separate from
 * call back list since the jobs here are different."* A callback is one row a person asked for.
 * A CRM run is a bulk query against somebody else's database that produces hundreds of calls
 * nobody individually asked for. Same account, different job, different screen.
 *
 * ## What this page cannot do, and says so
 *
 * `CRM_ORCHESTRATOR_ENABLED` and `DIAL_MODE` are read from the environment at import time, so
 * no button here can change them — a restart can. The poll interval lives in the customer's
 * config file for the same reason. Showing them as read-only facts with their source named is
 * honest; rendering them as controls that quietly do nothing is not.
 *
 * ## Why it is tabbed
 *
 * Because the lists on it do not stop growing. Dr Agarwals stores over a thousand records a
 * day and a fifteen-minute poll across a nine-hour window is thirty-six runs a day, so within a
 * week the single scrolling column was two enormous tables nobody could reach the bottom of.
 * Arun: *"this will be a big list after a few days. please ensure you eitehr put this in
 * separate tabs and paginate them oterhwise this page will not load well."* Both, then: one
 * list per tab, each asking the server for one page at a time.
 *
 * What stays outside the tabs is what is true regardless of which tab you are on — the two
 * switches and the halt. A halt means every poll is being skipped; finding that out depends on
 * which tab you happened to open is not a design, it is a trap.
 */

import { useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  FlaskConical,
  Info,
  Link2,
  Loader2,
  PauseCircle,
  PhoneOutgoing,
  Play,
  RefreshCw,
  ShieldBan,
  Timer,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { PageHeader } from "@/components/ui/page-header";
import { SettingsSection } from "@/components/ui/settings-section";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PaginationBar } from "@/components/ui/pagination-bar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { ADAPTER_LABEL, POLLER_LABEL } from "@/lib/crm-labels";
import {
  errorMessage,
  type OrchestratorCustomer,
  type OrchestratorLookup,
  type OrchestratorPreset,
  type OrchestratorRecord,
  type OrchestratorRecordStatus,
  type OrchestratorRun,
  type OrchestratorRunResult,
  type PlannedDial,
} from "@/lib/api";
import {
  useOrchestratorClearHalt,
  useOrchestratorPause,
  useOrchestratorResume,
  useOrchestratorCustomer,
  useOrchestratorRecords,
  useOrchestratorRun,
  useOrchestratorChannels,
  useOrchestratorRefreshChannels,
  useOrchestratorRuns,
  useOrchestratorSetChannel,
} from "@/lib/queries";
import { CrmCredentialsSection } from "./crm-credentials-section";

/**
 * How each stored status reads to somebody who did not write the ledger.
 *
 * `claimed` is the one worth spelling out: it means a run took the row and has not yet said
 * what happened, which for more than ten minutes means a process died mid-dial. Calling it
 * "claimed" on screen would make an operator go and read the source to find that out.
 */
const STATUS_LABEL: Record<OrchestratorRecordStatus, string> = {
  dialed: "Called",
  deferred: "Waiting",
  dropped: "Not calling",
  failed: "Failed",
  claimed: "In flight",
};

const STATUS_TONE: Record<OrchestratorRecordStatus, string> = {
  dialed: "text-success",
  deferred: "text-foreground",
  dropped: "text-muted-foreground",
  failed: "text-destructive",
  claimed: "text-foreground",
};

const PRESETS: { value: OrchestratorPreset; label: string }[] = [
  { value: "opt-today", label: "Today's appointments" },
  { value: "opt-yesterday", label: "Yesterday's appointments" },
  { value: "opt-last-seven-days", label: "The last seven days" },
  { value: "opt-all-time", label: "Everything the CRM has" },
];

/**
 * The four tabs, in the order somebody works through them.
 *
 * `run` leads because that is what an unproven feed needs first, and because landing on an
 * empty Records table is a worse first impression than landing on the button that fills it.
 */
const TABS = ["run", "records", "history", "how"] as const;
type Tab = (typeof TABS)[number];

export function CrmFeedPage() {
  const { accountId, customer: selected } = useParams<{ accountId: string; customer: string }>();
  const detail = useOrchestratorCustomer(selected);

  /*
    The tab lives in the URL so it survives a reload and can be linked to — "look at the run
    history on dragarwals" should be a link, not an instruction. The page numbers do not: they
    are where you got to while reading, not what the page is about, and five query parameters
    on a URL somebody is meant to paste into Slack is its own kind of mess.
  */
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? "") ? (tabParam as Tab) : "run";
  const setTab = (next: string) => {
    const copy = new URLSearchParams(params);
    if (next === "run") copy.delete("tab");
    else copy.set("tab", next);
    setParams(copy, { replace: true });
  };

  const [statusFilter, setStatusFilter] = useState<OrchestratorRecordStatus | "all">("all");
  const [recordPage, setRecordPage] = useState(0);
  const [recordLimit, setRecordLimit] = useState(25);
  const records = useOrchestratorRecords(selected, statusFilter, {
    limit: recordLimit,
    offset: recordPage * recordLimit,
  });

  const [historyPage, setHistoryPage] = useState(0);
  const [historyLimit, setHistoryLimit] = useState(20);
  /*
    Only fetched once its tab is open. The whole reason this stopped being a field on the feed
    response is that nobody should pay for a week of run history to read the page header.
  */
  const runs = useOrchestratorRuns(selected, {
    limit: historyLimit,
    offset: historyPage * historyLimit,
    enabled: tab === "history",
  });

  /*
    Defaults to whatever this feed actually runs on.

    A next-day batch never asks about today, so offering "Today's appointments" first means the
    obvious dry run reviews a window the timer will never use — and answers a question nobody
    asked. I hit this myself: the first dry run of the Tamil Nadu feed needed the preset passed
    by hand before it showed anything real.

    `null` until the config arrives, then set once from `polls_intraday`, so the reader is not
    switched out from under a choice they already made.
  */
  const [preset, setPreset] = useState<OrchestratorPreset | null>(null);
  const [lastRun, setLastRun] = useState<OrchestratorRunResult | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  /** Which of the two buttons is in flight, so the progress lands on the one that was pressed. */
  const [running, setRunning] = useState<"dry" | "real" | null>(null);

  const run = useOrchestratorRun(selected);
  const clearHalt = useOrchestratorClearHalt(selected);
  const [pauseNote, setPauseNote] = useState("");
  const pause = useOrchestratorPause(selected);
  const resume = useOrchestratorResume(selected);

  /** Changing what the list is showing has to send you back to its first page. */
  const pickStatus = (s: OrchestratorRecordStatus | "all") => {
    setStatusFilter(s);
    setRecordPage(0);
  };

  const cfg = detail.data?.customer;

  useEffect(() => {
    if (preset !== null || !cfg) return;
    setPreset(cfg.polls_intraday ? "opt-today" : "opt-yesterday");
  }, [cfg, preset]);
  const effectivePreset: OrchestratorPreset = preset ?? "opt-today";
  const switches = detail.data;
  /*
    Undefined while the feed is still loading, and `true` then, so the buttons are not briefly
    disabled with a reason that may turn out to be false. They are disabled on `run.isPending`
    anyway until the response lands.
  */
  const credentialsReady = detail.data?.credentials.ready ?? true;

  useEffect(() => {
    document.title = cfg ? `CRM outbound · ${cfg.label}` : "CRM outbound";
  }, [cfg]);

  const doRun = (dryRun: boolean) => {
    setLastRun(null);
    setLastMessage(null);
    setRunning(dryRun ? "dry" : "real");
    run.mutate(
      { dry_run: dryRun, preset: effectivePreset },
      {
        onSuccess: (res) => {
          setLastRun(res.run);
          setLastMessage(res.message);
          if (res.run.halted) toast.error(`Halted: ${res.run.halted.reason}`);
          else toast.success(res.message);
        },
        onError: (e) => toast.error(errorMessage(e)),
        onSettled: () => setRunning(null),
      },
    );
  };

  if (!accountId || !selected) return <Navigate to="/accounts" replace />;

  if (detail.isError) {
    return (
      <AppShell active="crm">
        <div className="p-6">
          <ErrorState
            title="Couldn't load this CRM feed"
            description={errorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell active="crm">
      <div className="flex h-full w-full max-w-full flex-col">
        <PageHeader
          /*
            Always, now that the list is always a real page: an account has feeds, and getting
            back to the others is the first thing anyone wants from inside one of them.
          */
          breadcrumb={[
            { label: "CRM outbound", href: `/accounts/${accountId}/crm` },
            { label: cfg?.label ?? selected },
          ]}
          /*
            The feed's name is the *job* — "No-show follow-up" — because one account has several
            of these and they would otherwise all be titled with the hospital's name. Which
            hospital is answered by the sidebar and by the crumb above.
          */
          title={cfg?.label ?? "CRM outbound"}
          description={
            cfg
              ? `${cfg.purpose} Read from the CRM on a timer — unlike the callback queue, nobody on this list asked to be rung.`
              : "Reads a CRM on a timer and calls the people in it."
          }
          actionsSlot={
            <>
              {/*
                The switch Arun asked for — *"how can i stop these runs when i want and reusme
                when i want?"* — in the header, because "is this feed on?" is a property of the
                feed rather than of any one tab, and because somebody reaching for it usually
                wants it now.

                And an actual switch, on his second look: *"can ou just put a toggle for enable
                / disable here? that will be more obcious and intuituve."* It had been two
                controls in two places — "Pause this feed" up here while it ran, "Resume this
                feed" buried in the alert that appeared when it did not — so the control moved
                depending on the state it was reporting, and neither instance said what the
                other state would be.

                The two directions are not symmetric and the confirmation says so: switching it
                **on** re-arms a timer that telephones real people, so it asks; switching it
                **off** stops calls, which is always the safe direction, and asks only to
                collect the note that later tells somebody why it is off.
              */}
              {cfg && detail.data ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!detail.data.pause}
                      aria-label={`${detail.data.pause ? "Enable" : "Pause"} ${cfg.label}`}
                      disabled={pause.isPending || resume.isPending}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors",
                          detail.data.pause ? "bg-muted-foreground/30" : "bg-success",
                        )}
                      >
                        <span
                          className={cn(
                            "bg-background size-3 rounded-full shadow transition-transform",
                            detail.data.pause ? "translate-x-0.5" : "translate-x-[14px]",
                          )}
                        />
                      </span>
                      <span className="text-sm">{detail.data.pause ? "Paused" : "Enabled"}</span>
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    {detail.data.pause ? (
                      <>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Enable {cfg.label}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            From then on the timer reads this CRM on its own schedule, and a run
                            telephones the people it selects. Nothing is dialled by pressing
                            this — the next scheduled run does that.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Leave it off</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              resume.mutate(undefined, {
                                onSuccess: (r) => toast.success(r.message),
                                onError: (e) => toast.error(errorMessage(e)),
                              })
                            }
                          >
                            Enable it
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </>
                    ) : (
                      <>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Pause {cfg.label}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            The timer stops asking this CRM for anything and no call goes out
                            from this feed. Dry runs keep working, so you can still check it.
                            Callbacks already in the queue are unaffected — this is not the
                            queue's pause. Nothing is lost while it is off.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="space-y-1.5">
                          <Label htmlFor="pause-note">Why (optional)</Label>
                          <Input
                            id="pause-note"
                            value={pauseNote}
                            onChange={(e) => setPauseNote(e.target.value)}
                            placeholder="e.g. their front desk asked us to hold for a week"
                          />
                          <p className="text-muted-foreground text-xs">
                            Shown next to the pause, so the next person does not have to guess
                            whether it is safe to turn back on.
                          </p>
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep it running</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() =>
                              pause.mutate(
                                { note: pauseNote.trim() || undefined },
                                {
                                  onSuccess: (r) => {
                                    setPauseNote("");
                                    toast.success(r.message);
                                  },
                                  onError: (e) => toast.error(errorMessage(e)),
                                },
                              )
                            }
                          >
                            Pause it
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </>
                    )}
                  </AlertDialogContent>
                </AlertDialog>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void detail.refetch();
                  void records.refetch();
                  // Only if its tab is open; refetching a query nobody has run yet would
                  // fire the request this page was restructured to avoid.
                  if (tab === "history") void runs.refetch();
                }}
                disabled={detail.isFetching || records.isFetching || runs.isFetching}
              >
                <RefreshCw
                  className={cn(
                    "size-3.5",
                    (detail.isFetching || records.isFetching || runs.isFetching) && "animate-spin",
                  )}
                />
                Reload this page
              </Button>
              {/*
                Arun: *"what dies the refresh button do here? not clear"*. Fair — on a page
                whose whole subject is reading somebody's CRM, a button called "Refresh" reads
                as "read the CRM again". It does not: it re-reads this page from this app.
                Said out loud rather than in a tooltip, because the wrong guess is the
                expensive one.
              */}
              <span className="text-muted-foreground self-center text-[11px]">
                does not ask the CRM
              </span>
            </>
          }
        />

        <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
          {detail.isPending ? (
            <div className="max-w-5xl space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <div className="max-w-5xl space-y-6">
              {/* ------------------------------------------- is it even on? --- */}
              {switches ? (
                <div className="space-y-3">
                  {switches.orchestrator_enabled ? (
                    <Alert>
                      <CheckCircle2 className="size-4" />
                      <AlertDescription>
                        <span className="font-medium">The timer is running.</span> This feed is
                        polled on the schedule below.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <Timer className="size-4" />
                      <AlertDescription>
                        <span className="font-medium">The timer is off,</span> so nothing polls
                        by itself and no CRM is being read.{" "}
                        <code>CRM_ORCHESTRATOR_ENABLED</code> is not set on the server. Runs you
                        start below still work — that is how this is meant to be exercised
                        before it is switched on.
                      </AlertDescription>
                    </Alert>
                  )}
                  {switches.can_dial_for_real ? (
                    <Alert variant="destructive">
                      <PhoneOutgoing className="size-4" />
                      <AlertDescription>
                        <span className="font-medium">Calls are real.</span>{" "}
                        <code>DIAL_MODE=live</code> — a run here telephones the people it
                        selects.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <FlaskConical className="size-4" />
                      <AlertDescription>
                        <span className="font-medium">Calls are simulated.</span>{" "}
                        <code>DIAL_MODE={switches.dial_mode}</code> — a run assembles every call
                        body and places none. Rows are still stored, so everything on this page
                        is real except the telephony.
                      </AlertDescription>
                    </Alert>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Both are environment variables read once at startup. Nothing on this page
                    can change them; the server has to restart.
                  </p>
                </div>
              ) : null}

              {/* ---------------------------------------- stopped by a person --- */}
              {/*
                Arun: *"i need a way to enable or disable the crm … how can i stop these runs
                when i want and reusme when i want?"*

                Above the halt and styled as information rather than an incident, because that
                is the difference between the two: a halt is the feed stopping itself over
                something broken, a pause is somebody deciding it should stop. Both skip the
                poll; only one of them is news.
              */}
              {cfg && detail.data?.pause ? (
                <Alert>
                  <PauseCircle className="size-4" />
                  <AlertDescription className="space-y-3">
                    <p>
                      <span className="font-medium">
                        Paused {DateTime.fromISO(detail.data.pause.paused_at).toRelative() ?? ""}
                        {detail.data.pause.paused_by_email
                          ? ` by ${detail.data.pause.paused_by_email}`
                          : ""}
                      </span>
                      {detail.data.pause.note ? ` — ${detail.data.pause.note}` : ""}
                    </p>
                    <p className="text-sm">
                      The timer skips this feed without asking the CRM for anything, and a real
                      run is refused. Dry runs still work — that is how you check a paused feed
                      without letting it dial. Callbacks already in the queue are unaffected:
                      pausing this does not pause the queue.
                    </p>
                    {/*
                      No Resume button here any more. This alert's job is saying *why* it is
                      off, which is worth keeping; turning it on belongs to the switch in the
                      header, where the state is shown. One setting, one control.
                    */}
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* ------------------------------------------------ the halt --- */}
              {detail.data?.halt ? (
                <Alert variant="destructive">
                  <ShieldBan className="size-4" />
                  <AlertDescription className="space-y-3">
                    <p>
                      <span className="font-medium">
                        Halted{" "}
                        {DateTime.fromISO(detail.data.halt.halted_at).toRelative() ?? ""}
                      </span>{" "}
                      — {detail.data.halt.reason}
                      {detail.data.halt.code ? ` (${detail.data.halt.code})` : ""}
                    </p>
                    <p className="text-sm">
                      This is a latch. Every poll for this customer is skipped without touching
                      the CRM until somebody clears it, because a halt means a config error that
                      would repeat on every row.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={clearHalt.isPending}
                      onClick={() =>
                        clearHalt.mutate(undefined, {
                          onSuccess: (r) => toast.success(r.message),
                          onError: (e) => toast.error(errorMessage(e)),
                        })
                      }
                    >
                      Clear the halt
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* --------------------------- can it read the CRM at all? --- */}
              {/*
                Above the tabs, with the halt and the switches, because it is true whichever tab
                you are on and because it gates every one of them: a feed with no keys has no
                records, no history and a Dry run that fails twelve seconds in with LeadSquared's
                own words. Answered here, in one line when the answer is yes.
              */}
              {cfg && detail.data ? (
                <CrmCredentialsSection
                  customer={cfg.customer}
                  label={cfg.label}
                  credentials={detail.data.credentials}
                />
              ) : null}

              {/*
                Which channel this feed dials on, and the way to change it.

                Arun: *"can you also let me select an agent channel from the UI for each crm
                outbound? you need a get latest channels and let me select from a drop down of
                agent cahnnels for this account."* And: *"each crm outbound can use different
                channels btw."*

                Next to the CRM keys because they are the same question from the two ends —
                where this feed reads from, and where it calls out on. Both were invisible
                before: the keys lived in an env var and the channel in a config file, so the
                page could not answer either.
              */}
              {cfg && detail.data ? (
                <CrmChannelSection customer={cfg.customer} label={cfg.label} />
              ) : null}

              {detail.isPending || !cfg ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                /*
                  The tab list below fixes the order they appear in; the panels are written out in
                  the same order beneath it. Nothing here is lazy except the run history, which is
                  the only one whose query is gated on its own tab being open.
                */
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList>
                    <TabsTrigger value="run">Run it now</TabsTrigger>
                    <TabsTrigger value="records">
                      Stored records
                      {(records.data?.stats.total ?? 0) > 0 ? (
                        <span className="ml-1.5 tabular-nums opacity-60">
                          {(records.data?.stats.total ?? 0).toLocaleString()}
                        </span>
                      ) : null}
                    </TabsTrigger>
                    <TabsTrigger value="history">Run history</TabsTrigger>
                    <TabsTrigger value="how">How this feed works</TabsTrigger>
                  </TabsList>

                  <TabsContent value="run" className="mt-4">
                    {/* ----------------------------------- run it, right now --- */}
                    <SettingsSection
                      title="Run it now"
                      description="One pass of the whole pipeline, without waiting for the timer."
                    >
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium" htmlFor="crm-preset">
                            Which appointments to ask the CRM for
                          </label>
                          <Select
                            value={effectivePreset}
                            onValueChange={(v) => setPreset(v as OrchestratorPreset)}
                          >
                            <SelectTrigger id="crm-preset" className="w-[280px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRESETS.map((p) => (
                                <SelectItem key={p.value} value={p.value}>
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Dry run is the primary, and that is not a style choice: the dry run
                            *is* how a feed is reviewed. "Run for real" had the filled button
                            and pulled the eye to the one action that stores rows and, with
                            DIAL_MODE live, rings strangers.

                            The progress goes on the button that was actually pressed. Both were
                            disabled while a run was in flight but only "Run for real" changed
                            its label, so pressing Dry run appeared to start the other one. */}
                        <Button
                          onClick={() => doRun(true)}
                          disabled={run.isPending || !credentialsReady}
                        >
                          {running === "dry" ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <FlaskConical className="size-4" />
                          )}
                          {running === "dry" ? "Reading the CRM…" : "Dry run"}
                        </Button>
                        {/* A real run writes rows and, with DIAL_MODE live, telephones people —
                            so it is the quieter button, and it still confirms. */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              disabled={run.isPending || !credentialsReady}
                            >
                              {running === "real" ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Play className="size-4" />
                              )}
                              {running === "real" ? "Running…" : "Run for real"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Run {cfg.label} for real?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {switches?.can_dial_for_real
                                  ? `DIAL_MODE is live. This reads ${cfg.label}'s CRM and telephones up to ${cfg.max_dials_per_run} people who match, for real, right now.`
                                  : `This makes one real read of ${cfg.label}'s CRM and stores every row it finds — including real phone numbers. It places no calls: DIAL_MODE is ${switches?.dial_mode ?? "dry_run"}, so each row is claimed and handed straight back.`}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => doRun(false)}>
                                Run it
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        {/* The reason sits beside the disabled buttons, not in a tooltip: two
                            dead buttons and no explanation is the state this page was in
                            before there was anywhere to put the keys. */}
                        {!credentialsReady ? (
                          <p className="text-warning basis-full text-xs">
                            Both are disabled until this feed has its CRM keys — enter them
                            under <span className="font-medium">Can this feed read the CRM?</span>{" "}
                            above.
                          </p>
                        ) : null}
                      </div>
                      {/*
                        This note is here because a run looked broken without it. "The last seven
                        days" of Dr Agarwals is 20,210 rows over 21 pages and took 4m50s — the
                        button sat busy with no explanation, which reads as a hang, not as work.
                      */}
                      <p className="text-muted-foreground text-xs">
                        A dry run reads the CRM for real and writes nothing at all: it stops at the
                        moment before the first call and tells you what it would have done.{" "}
                        <span className="text-foreground font-medium">
                          Anything broader than one day takes minutes
                        </span>{" "}
                        — the range is paged through in full, which for a busy customer is tens of
                        thousands of rows. The button stays busy until it finishes; if you leave
                        the page the run carries on and shows up under Run history.
                      </p>
                      {/*
                        Keyed on the run so a second run resets the planned list back to its
                        first page. Without it, dry-running a narrower range while parked on
                        page 4 shows an empty table and looks like the run found nothing.
                      */}
                      {lastRun ? (
                        <RunOutcome
                          key={lastRun.runId ?? "no-run-id"}
                          run={lastRun}
                          message={lastMessage}
                          windowLabel={cfg.window}
                        />
                      ) : null}
                    </SettingsSection>
                  </TabsContent>

                  <TabsContent value="records" className="mt-4">
                    {/* ------------------------------ the rows, with numbers --- */}
                    <SettingsSection
                      title="Stored records"
                      description="One row per person per appointment day — that uniqueness is the dedupe."
                    >
                      {records.isPending ? (
                        <Skeleton className="h-40 w-full" />
                      ) : records.isError ? (
                        <ErrorState
                          title="Couldn't load records"
                          description={errorMessage(records.error)}
                          onRetry={() => void records.refetch()}
                        />
                      ) : (
                        <>
                          {/* Filters and the dedupe count only once there is something to
                              filter. "All 0" beside "0 of 0 came back on a later poll" is three
                              controls describing an empty table. */}
                          {(records.data?.stats.total ?? 0) > 0 ? (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex flex-wrap gap-2">
                                <StatusChip
                                  label="All"
                                  n={records.data?.stats.total ?? 0}
                                  active={statusFilter === "all"}
                                  onClick={() => pickStatus("all")}
                                />
                                {(
                                  ["dialed", "deferred", "dropped", "failed", "claimed"] as const
                                ).map((s) => {
                                  const n =
                                    records.data?.stats.by_status.find((b) => b.status === s)?.n ??
                                    0;
                                  if (n === 0 && statusFilter !== s) return null;
                                  return (
                                    <StatusChip
                                      key={s}
                                      label={STATUS_LABEL[s]}
                                      n={n}
                                      active={statusFilter === s}
                                      onClick={() => pickStatus(s)}
                                    />
                                  );
                                })}
                              </div>
                              {/* The dedupe evidence. A unique constraint doing its job is
                                  invisible; this counts the rows it quietly protected. */}
                              <p className="text-muted-foreground text-xs">
                                {records.data?.stats.reseen ?? 0} of{" "}
                                {records.data?.stats.total ?? 0} came back on a later poll and were
                                updated in place, not duplicated.
                              </p>
                            </div>
                          ) : null}

                          {(records.data?.records.length ?? 0) === 0 ? (
                            <EmptyState
                              size="compact"
                              title={
                                statusFilter === "all"
                                  ? "Nothing stored yet"
                                  : `No ${STATUS_LABEL[statusFilter].toLowerCase()} rows`
                              }
                              /*
                                Worth being precise here rather than hopeful, because the
                                obvious next move — press Dry run and come back — leaves this
                                table exactly as empty. Storing a row *is* claiming the dial;
                                that is what stops two processes ringing the same person, and
                                it is the one thing a dry run must not do.
                              */
                              description={
                                statusFilter === "all"
                                  ? `Nothing has written to this feed's ledger. A dry run deliberately writes nothing at all — the list it would call is shown on the Run it now tab instead. Rows land here when the timer polls this feed, or after Run for real.${
                                      switches?.can_dial_for_real
                                        ? ""
                                        : " With DIAL_MODE not live that stores the rows and still telephones nobody."
                                    }`
                                  : "Try another status."
                              }
                            />
                          ) : (
                            <>
                              <RecordTable records={records.data?.records ?? []} />
                              {/*
                                Flush to the bottom of the card: the section's body carries the
                                padding, so the negative margins let the bar's own divider span
                                the full card width instead of floating inside it.
                              */}
                              <PaginationBar
                                className="-mx-6 -mb-5"
                                page={recordPage}
                                limit={recordLimit}
                                total={records.data?.total ?? 0}
                                onPageChange={setRecordPage}
                                onLimitChange={(n) => {
                                  setRecordLimit(n);
                                  setRecordPage(0);
                                }}
                                limitOptions={[25, 50, 100, 200]}
                              />
                            </>
                          )}
                        </>
                      )}
                    </SettingsSection>
                  </TabsContent>

                  <TabsContent value="history" className="mt-4">
                    {/* --------------------------------------- the run history --- */}
                    <SettingsSection
                      title="Every run"
                      description="Every pass, scheduled or by hand, newest first."
                    >
                      {runs.isPending ? (
                        <Skeleton className="h-40 w-full" />
                      ) : runs.isError ? (
                        <ErrorState
                          title="Couldn't load the run history"
                          description={errorMessage(runs.error)}
                          onRetry={() => void runs.refetch()}
                        />
                      ) : (runs.data?.runs.length ?? 0) === 0 ? (
                        <EmptyState
                          size="compact"
                          title="No runs recorded"
                          description="Nothing has polled this customer yet — not the timer, not by hand. A dry run counts: it is recorded here even though it writes no records."
                        />
                      ) : (
                        <>
                          <RunTable runs={runs.data?.runs ?? []} />
                          <p className="text-muted-foreground text-xs">
                            <span className="text-foreground font-medium">Started</span> is the
                            clock the run's rules used, which for a replay is the time it was told
                            to pretend it was — not when it was launched.
                          </p>
                          <PaginationBar
                            className="-mx-6 -mb-5"
                            page={historyPage}
                            limit={historyLimit}
                            total={runs.data?.total ?? 0}
                            onPageChange={setHistoryPage}
                            onLimitChange={(n) => {
                              setHistoryLimit(n);
                              setHistoryPage(0);
                            }}
                            limitOptions={[20, 50, 100]}
                          />
                        </>
                      )}
                    </SettingsSection>
                  </TabsContent>

                  <TabsContent value="how" className="mt-4">
                    {/* ------------------------------ what it does, in order --- */}
                    <SettingsSection
                      title="How this feed works"
                      description="What happens on every poll, in the order it happens."
                    >
                      <Pipeline cfg={cfg} />
                      <p className="text-muted-foreground border-t pt-4 text-xs">
                        Every value here comes from{" "}
                        <code>server/src/orchestrator/customers/{cfg.customer}.ts</code>. To change
                        one — the interval, the calling window, which rows count — open this repo in
                        Claude Code and ask for the change by name; it deploys with the next
                        release. There is deliberately no way to retune a live customer's dialling
                        from a browser.
                      </p>
                    </SettingsSection>
                  </TabsContent>

                </Tabs>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ------------------------------------------------------------------ pipeline ---

/**
 * The five stages, as sentences.
 *
 * A config dump was the obvious thing to render here and would have been the wrong one:
 * `call_after_event_minutes: 90` is not an answer to "when does it ring", it is a fact you
 * then have to interpret. The numbers are all still on screen, inside the sentence that says
 * what each one does.
 */
function Pipeline({ cfg }: { cfg: OrchestratorCustomer }): React.ReactElement {
  const stages: { icon: React.ReactElement; head: string; body: React.ReactNode }[] = [
    {
      icon: <Database className="size-4" />,
      head: "Fetch",
      body: (
        <>
          Asks {ADAPTER_LABEL[cfg.source_adapter] ?? cfg.source_adapter} for appointments every{" "}
          <Strong>{cfg.poll_every_minutes} minutes</Strong>, but only between{" "}
          <Strong>{cfg.window}</Strong>.
          {cfg.reconcile_at_open ? (
            <>
              {" "}
              Once more at opening time it re-reads <Strong>yesterday</Strong>, because by
              morning the CRM has settled its own outcome column and the evening's last rows
              were never reached.
            </>
          ) : null}{" "}
          {/*
            Said here because "on a schedule" is only half of what Arun asked for — *"i want to
            run calls from a customers system when certain events happen or on a schedule"* — and
            a page that shows only the timer implies the other half exists somewhere else on it.
          */}
          Pausing this feed stops both. A trigger the customer's own system calls when something
          happens is not built yet, so the schedule is the only way in.
        </>
      ),
    },
    /*
      A `before_rules` join runs here, ahead of the consent gate, because that is the only reason
      to pay for it early: the gate itself needs the value. Rendered as its own numbered step in
      its real position rather than as one "extra APIs" box — the order is the design, and a step
      drawn in the wrong place is a wrong explanation.
    */
    ...fillStep(
      cfg.lookups.filter((l) => l.stage === "before_rules"),
      cfg.derive?.stage === "before_rules" ? cfg.derive : null,
      "before the consent and eligibility rules, because those rules read what it returns",
    ),
    {
      icon: <ShieldBan className="size-4" />,
      head: "Filter",
      body: (
        <>
          {cfg.fetch_only_when.length > 0 ? (
            <>
              The CRM is only asked for rows where{" "}
              <Cond>{cfg.fetch_only_when.join(" and ")}</Cond>, so the rest never crosses the
              wire.{" "}
            </>
          ) : null}
          {cfg.suppress_when.length > 0 ? (
            <>
              Of what comes back, anyone with <Cond>{cfg.suppress_when.join(" or ")}</Cond> is
              dropped. If that column cannot be read at all the whole run halts rather than
              guess — a consent check that fails open is not a consent check.
            </>
          ) : (
            <>
              This customer has no do-not-call column configured, which means nothing here is
              screening for consent.
            </>
          )}
        </>
      ),
    },
    {
      icon: <Clock className="size-4" />,
      head: "Wait",
      body: (
        <>
          Nobody is called until <Strong>{cfg.call_after_event_minutes} minutes</Strong> after
          their appointment slot — long enough that a late arrival is not rung mid-visit.
          {cfg.max_event_age_minutes !== null ? (
            <>
              {" "}
              Appointments older than <Strong>{cfg.max_event_age_minutes} minutes</Strong> are
              left alone.
            </>
          ) : null}{" "}
          Rule: <code>{cfg.eligibility_rule}</code>.
        </>
      ),
    },
    {
      icon: <CheckCircle2 className="size-4" />,
      head: "Dedupe",
      body: (
        <>
          <Strong>One call per person per appointment day</Strong>, enforced by a database
          constraint rather than by remembering. The same appointment comes back on every poll
          for hours — that repetition is deliberate, it is how a row edited or added late gets
          picked up — and it can only ever produce one call. A row that is not called within{" "}
          <Strong>{cfg.max_age_days} days</Strong> is dropped.
        </>
      ),
    },
    ...fillStep(
      cfg.lookups.filter((l) => l.stage === "before_dial"),
      cfg.derive?.stage === "before_dial" ? cfg.derive : null,
      "only for the rows about to be dialled, which is usually a twentyfold saving",
    ),
    {
      icon: <PhoneOutgoing className="size-4" />,
      head: "Dial",
      body: (
        <>
          Through 8loop agent <code>{cfg.agent_id}</code> on the{" "}
          <code>{cfg.account_slug}</code> account, at most{" "}
          <Strong>{cfg.max_dials_per_run}</Strong> per run. Each call carries{" "}
          {cfg.mapped_fields.length} conversation fields:{" "}
          <span className="font-mono text-xs">{cfg.mapped_fields.join(", ")}</span>.
        </>
      ),
    },
  ];

  return (
    <ol className="space-y-4">
      {/* Indexed key: a feed can have two "Fill in the rest" steps, one at each stage. */}
      {stages.map((s, i) => (
        <li key={`${i}-${s.head}`} className="flex gap-3">
          <span className="bg-muted text-muted-foreground mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium">
            {i + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-sm font-semibold">
              {s.icon}
              {s.head}
            </p>
            {/* A <div>, not a <p>: the lookup step's body is a list, and a <ul> inside a <p> is
                invalid HTML that React reports as a nesting error at runtime. */}
            <div className="text-muted-foreground mt-0.5 text-sm">{s.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * The extra-API step, or nothing at all.
 *
 * Arun: *"each orchestration has one or more apis to call … the details needed to make the calls
 * may not be availale in one single api."* A feed that reads three endpoints and a page that
 * mentions one is a page describing a different service — so every join gets a line, with the
 * endpoint, what keys it, and which field names it contributes.
 *
 * Returns an array so the caller can spread it: most feeds need one API and get no step, and
 * their pipeline stays five plain stages. Dr Agarwals is one of those.
 *
 * `when` is the sentence fragment explaining why this stage rather than the other one, which is
 * the single most confusing thing about lookups and the one thing config alone cannot show.
 */
function fillStep(
  lookups: OrchestratorLookup[],
  derive: { summary: string; provides: string[] } | null,
  when: string,
): { icon: React.ReactElement; head: string; body: React.ReactNode }[] {
  if (lookups.length === 0 && !derive) return [];
  return [
    {
      icon: <Link2 className="size-4" />,
      head: lookups.length > 0 ? "Fill in the rest" : "Work it out",
      body: (
        <>
          {lookups.length > 0 ? (
            <>
              The list does not carry everything the agent needs, so each row is completed from{" "}
              <Strong>
                {lookups.length} further {lookups.length === 1 ? "API" : "APIs"}
              </Strong>
              , {when}:
              <ul className="mt-1.5 space-y-1.5">
                {lookups.map((l) => (
                  <li key={l.as} className="text-xs">
                    <code>
                      {l.method} {l.path}
                    </code>{" "}
                    —{" "}
                    {l.kind === "table"
                      ? "the whole reference list, fetched once per run"
                      : "one call per distinct value, cached for the run"}
                    , keyed on <code>{l.key_from}</code>. Adds{" "}
                    <span className="font-mono">{l.provides.join(", ")}</span>.{" "}
                    {l.required
                      ? "A key it cannot answer takes that row out of this run."
                      : "A key it cannot answer leaves those fields blank."}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {derive ? (
            <p className={lookups.length > 0 ? "mt-2" : undefined}>
              A code step runs here too: {derive.summary}
              {derive.provides.length > 0 ? (
                <>
                  {" "}
                  It adds <span className="font-mono text-xs">{derive.provides.join(", ")}</span>.
                </>
              ) : null}
            </p>
          ) : null}
        </>
      ),
    },
  ];
}

const Strong = ({ children }: { children: React.ReactNode }) => (
  <span className="text-foreground font-medium">{children}</span>
);

/** A CRM column condition, verbatim from the config. Monospace because it names a column. */
const Cond = ({ children }: { children: React.ReactNode }) => (
  <code className="text-foreground">{children}</code>
);

// ------------------------------------------------------------------- records ---

function StatusChip({
  label,
  n,
  active,
  onClick,
}: {
  label: string;
  n: number;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "focus-visible:ring-ring rounded-md border px-2.5 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none",
        active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent",
      )}
    >
      {label} <span className="opacity-70">{n}</span>
    </button>
  );
}

/**
 * The table Arun asked for: real numbers, real dates, and what happened to each row.
 *
 * `phone_e164` is shown in full, deliberately. The point of the screen is confirming that a
 * CRM's `9000012345` became `+919000012345` — a masked column cannot answer that, and the
 * route is admin-only for exactly this reason.
 */
function RecordTable({ records }: { records: OrchestratorRecord[] }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Number</TableHead>
            <TableHead>Appointment</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Tries</TableHead>
            <TableHead>Last thing that happened</TableHead>
            <TableHead>In the CRM</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="align-top font-mono whitespace-nowrap">
                {r.phone_e164}
              </TableCell>
              <TableCell className="align-top whitespace-nowrap">
                <span>{r.occurrence_date}</span>
                {r.occurred_at ? (
                  <span className="text-muted-foreground block text-xs">
                    {DateTime.fromISO(r.occurred_at).toFormat("HH:mm")}
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="align-top whitespace-nowrap">
                <span className={cn("text-sm font-medium", STATUS_TONE[r.status])}>
                  {STATUS_LABEL[r.status]}
                </span>
                {r.call_id ? (
                  <span className="text-muted-foreground block font-mono text-xs">
                    {r.call_id}
                  </span>
                ) : null}
                {r.deferred_until ? (
                  <span className="text-muted-foreground block text-xs">
                    retry {DateTime.fromISO(r.deferred_until).toRelative()}
                  </span>
                ) : null}
                {/*
                  A claim is meant to last seconds. One that has sat there for an hour means the
                  process holding it died, and nothing else on the row says so — the status alone
                  reads like normal work in progress, which is how a stuck row goes unnoticed.
                */}
                {r.status === "claimed" &&
                DateTime.fromISO(r.last_seen_at).diffNow("hours").hours < -1 ? (
                  <span className="text-destructive block text-xs">stuck — nothing came back</span>
                ) : null}
              </TableCell>
              <TableCell className="align-top text-right tabular-nums">{r.attempt_n}</TableCell>
              {/*
                `TableCell` bakes in `whitespace-nowrap`, so a long reason ran clean over the
                next column — `max-w-*` on the cell did nothing, because an auto-layout table
                gives a column the width its unwrappable content demands. Two things are needed:
                `whitespace-normal` to allow a break at all, and a real width on a block child
                so the column has something to wrap against.
              */}
              <TableCell className="align-top whitespace-normal">
                <div className="w-[300px] min-w-[300px] space-y-1">
                  <p className="text-sm break-words">{r.last_reason ?? "—"}</p>
                  {r.last_error_code ? (
                    <Badge variant="secondary" className="font-mono">
                      {r.last_error_code}
                    </Badge>
                  ) : null}
                  <p className="text-muted-foreground text-xs">
                    {DateTime.fromISO(r.last_seen_at).toRelative()}
                    {r.last_seen_at > r.first_seen_at ? " · seen again since first stored" : null}
                    {r.poller ? ` · ${POLLER_LABEL[r.poller] ?? r.poller}` : ""}
                  </p>
                </div>
              </TableCell>
              <TableCell className="align-top whitespace-normal">
                <span className="font-mono text-xs">{r.record_id}</span>
                {Object.keys(r.fields).length > 0 ? (
                  <details className="mt-1">
                    {/* nowrap on the toggle only: the cell allows wrapping for a long record
                        id and for the field values, but "2 fields sent" broken across two
                        lines just looks like a rendering fault. */}
                    <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs whitespace-nowrap select-none">
                      {Object.keys(r.fields).length} fields sent
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                      {Object.entries(r.fields).map(([k, v]) => (
                        <li key={k} className="text-xs">
                          <span className="text-muted-foreground font-mono">{k}</span>{" "}
                          <span className="font-mono">{v || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------- runs ---

/**
 * An unfinished run old enough that it is not going to finish.
 *
 * An hour, not ten minutes: a whole-history read of a busy customer legitimately takes several
 * minutes, so a short threshold would libel a run that was still working.
 *
 * Measured against `started_at`, which is the logical clock — so a run *currently* in flight
 * with a backdated `now` reads as stalled. That is the one false positive, it needs the `now`
 * parameter to happen at all, and the wording hedges rather than asserts.
 */
function stalled(r: OrchestratorRun): boolean {
  return r.finished_at === null && DateTime.fromISO(r.started_at).diffNow("hours").hours < -1;
}

/**
 * `Started` is the clock the run's *rules* read, not when somebody pressed the button.
 *
 * `crm_runs.started_at` is written from the run's logical `now`, which a replay can set to any
 * instant — so subtracting it from `finished_at` produced durations like "26572s" for a run
 * that took twenty seconds. A wall-clock finish time next to a logical start looks odd but is
 * true; a computed elapsed time was neither.
 */
function RunTable({ runs }: { runs: OrchestratorRun[] }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Started</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead className="text-right">Fetched</TableHead>
            <TableHead className="text-right">Callable</TableHead>
            <TableHead className="text-right">Called</TableHead>
            <TableHead className="text-right">Held back</TableHead>
            <TableHead>Outcome</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((r) => {
            const c = r.counts;
            const held =
              (c.deferredWindow ?? 0) + (c.deferredBudget ?? 0) + (c.deferredRateLimit ?? 0);
            return (
              <TableRow key={r.id}>
                <TableCell className="whitespace-nowrap">
                  {DateTime.fromISO(r.started_at).toFormat("d LLL HH:mm")}
                  <span className="text-muted-foreground block text-xs">
                    {r.finished_at
                      ? `finished ${DateTime.fromISO(r.finished_at).toFormat("d LLL HH:mm")}`
                      : "no finish recorded"}
                  </span>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {POLLER_LABEL[r.poller] ?? r.poller}
                  {r.dry_run ? (
                    <Badge variant="secondary" className="ml-2">
                      wrote nothing
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.rows_fetched ?? "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{c.eligible ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{c.dialed ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{held || "—"}</TableCell>
                <TableCell className="max-w-[280px]">
                  {r.halted_reason ? (
                    <span className="text-destructive text-sm">
                      halted — {r.halted_reason}
                    </span>
                  ) : r.error ? (
                    <span className="text-destructive text-sm">{r.error}</span>
                  ) : r.ok === null ? (
                    /*
                      A run with no verdict is either in flight or was killed before it could
                      write one — a deploy, a `tsx watch` restart, a crash. Both look identical
                      in the row, and "no verdict yet" on a row from last Tuesday is a lie of
                      omission, so after an hour it says what almost certainly happened.
                    */
                    stalled(r) ? (
                      <span className="text-destructive text-sm">
                        never finished — the process was probably restarted mid-run
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">still running</span>
                    )
                  ) : r.ok ? (
                    <span className="text-success text-sm">finished cleanly</span>
                  ) : (
                    <span className="text-destructive text-sm">finished with errors</span>
                  )}
                  {c.dropped ? (
                    <span className="text-muted-foreground block text-xs">
                      {c.dropped} dropped
                      {c.duplicates ? `, ${c.duplicates} duplicate rows collapsed` : ""}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * A reason histogram, grouped, capped and folded away.
 *
 * Rendered flat this was 81 lines of which 77 were the same sentence with a different number:
 * `not due yet: the 90min call-after delay has not elapsed (140min to go)`. The engine puts the
 * per-row detail inside the reason string, which makes every row its own key — excellent in a
 * log, unreadable as a list. So the trailing parenthetical is stripped for grouping only, and
 * the count of folded variants is stated rather than hidden.
 *
 * Collapsed by default because this is the second question, not the first: the funnel above
 * says *where* rows went, and only then does anyone want to know why.
 */
function ReasonList({
  title,
  note,
  reasons,
}: {
  title: string;
  note?: string;
  reasons: Record<string, number>;
}): React.ReactElement | null {
  const entries = Object.entries(reasons);
  if (entries.length === 0) return null;

  const grouped = new Map<string, { n: number; variants: number }>();
  for (const [reason, n] of entries) {
    const key = reason.replace(/\s*\([^()]*\)\s*$/, "");
    const prev = grouped.get(key);
    grouped.set(key, { n: (prev?.n ?? 0) + n, variants: (prev?.variants ?? 0) + 1 });
  }
  const rows = [...grouped.entries()].sort((a, b) => b[1].n - a[1].n);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);

  return (
    <details className="border-t pt-3">
      <summary className="hover:text-foreground cursor-pointer text-sm font-medium select-none">
        {title}{" "}
        <span className="text-muted-foreground font-normal">
          — {total.toLocaleString()} row{total === 1 ? "" : "s"}, {rows.length} reason
          {rows.length === 1 ? "" : "s"}
        </span>
      </summary>
      {note ? <p className="text-muted-foreground mt-2 text-xs">{note}</p> : null}
      <ul className="mt-2 space-y-1">
        {rows.map(([reason, { n, variants }]) => (
          <li key={reason} className="text-muted-foreground flex gap-2 text-xs">
            <span className="w-14 shrink-0 text-right tabular-nums">{n.toLocaleString()}</span>
            <span className="min-w-0">
              {reason}
              {variants > 1 ? (
                <span className="opacity-60"> ({variants} variants folded together)</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * What the run just did, funnel-shaped.
 *
 * Ordered as a funnel because the useful question is never one number, it is where the drop
 * happened: 1,280 fetched and 4 callable is either correct or a broken filter, and the two
 * look identical unless the stages between them are on screen.
 */
function RunOutcome({
  run,
  message,
  windowLabel,
}: {
  run: OrchestratorRunResult;
  message: string | null;
  /** Pre-formatted, e.g. `10:30–11:00 Asia/Kolkata`. Named so the deferral has a reason. */
  windowLabel: string;
}): React.ReactElement {
  const c = run.counts;
  const funnel: { label: string; n: number; note?: string }[] = [
    { label: "Fetched from the CRM", n: c.rowsFetched, note: `${c.pages} page${c.pages === 1 ? "" : "s"}` },
    { label: "Lost in normalisation", n: c.normalizeDropped, note: "no usable number or date" },
    { label: "Do-not-call", n: c.suppressed },
    { label: "Not their turn yet", n: c.ineligibleNotYet },
    { label: "Never callable", n: c.ineligiblePermanent },
    { label: "Duplicates collapsed", n: c.duplicates },
    { label: "Too old", n: c.stale },
    { label: "Callable", n: c.eligible },
    { label: "Called", n: c.dialed, note: c.replayed ? `${c.replayed} replayed` : undefined },
    {
      label: "Outside calling hours",
      n: c.deferredWindow,
      // Without this the row is a dead end — the biggest number in the funnel with nothing
      // said about what becomes of it. They are queued, not lost, and the list below is them.
      note: run.plannedDeferredUntil ? "queued for the next open, listed below" : undefined,
    },
    { label: "Over the run's cap", n: c.deferredBudget },
    { label: "Rate limited", n: c.deferredRateLimit },
    { label: "Dropped", n: c.dropped },
    { label: "Failed", n: c.failed },
  ];
  const shown = funnel.filter((f) => f.n > 0 || f.label === "Callable" || f.label === "Called");

  return (
    <div className="space-y-4 rounded-md border p-4">
      {message ? (
        <p className={cn("text-sm", run.halted || run.error ? "text-destructive" : "")}>
          {message}
        </p>
      ) : null}
      {run.error && run.error !== message ? (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{run.error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {shown.map((f) => (
          <div key={f.label} className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              {f.label}
              {f.note ? <span className="ml-1 text-xs opacity-70">({f.note})</span> : null}
            </span>
            {/* Grouped: a real day of this feed is four digits, and 2831 reads as a code. */}
            <span className="tabular-nums">{f.n.toLocaleString()}</span>
          </div>
        ))}
      </div>
      <ReasonList title="Why rows were not called" reasons={run.dropReasons} />
      <ReasonList title="Why the callable rows were callable" reasons={run.eligibleReasons} />
      <ReasonList
        title="Fields that arrived as a shared placeholder"
        note="A pooled value is one the CRM uses for everybody, so it identifies nothing. Worth knowing before an agent reads it out."
        reasons={run.pooled}
      />
      <ReasonList
        title="Fields that arrived blank"
        note="The agent still gets the field, empty. If a script depends on it, this is where that breaks."
        reasons={run.blankFields}
      />
      {run.planned.length > 0 ? (
        <PlannedList
          planned={run.planned}
          total={run.plannedTotal}
          callable={c.eligible}
          deferredUntil={run.plannedDeferredUntil}
          windowLabel={windowLabel}
        />
      ) : null}
    </div>
  );
}

/**
 * The list a dry run produced: who it would ring, and what it would tell the agent about them.
 *
 * This is the point of the whole page for a feed that is not live yet. Arun, on how he intends
 * to review one: *"i dont want to do run for real, i can do dry run to see if the list
 * populates."* So it is the actual list, paged, with the mapped conversation field values
 * spelled out — because "what calls are made" means the request body, and a field that arrived
 * as the wrong branch name looks exactly like a correct one until you read it.
 *
 * Paged in the browser rather than by the server: the whole array already arrived with the run
 * result, so a second request to look at rows 26–50 of something already in memory would be
 * theatre.
 */
function PlannedList({
  planned,
  total,
  callable,
  deferredUntil,
  windowLabel,
}: {
  planned: PlannedDial[];
  total: number;
  /** How many rows survived every rule. Above `total` when the run's own cap held some back. */
  callable: number;
  /**
   * Set when the run was outside its calling window, so this list is what would go out at the
   * next open rather than what would have gone out on this pass. The distinction is the whole
   * difference between "it would have rung these people" and "it will ring these people".
   */
  deferredUntil: string | null;
  /** The feed's own window, pre-formatted, so the deferral names the reason. */
  windowLabel: string;
}): React.ReactElement {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const rows = planned.slice(page * limit, page * limit + limit);
  const held = Math.max(callable - total, 0);

  return (
    <div className="space-y-3 border-t pt-3">
      <div>
        <p className="flex items-center gap-2 text-sm font-medium">
          <Info className="size-3.5" />
          {deferredUntil ? "Who it would call" : "Who it would have called"}
        </p>
        {/*
          `total` and not `planned.length`: the response describes at most five hundred rows
          individually, and reporting a capped list as a count is how an honest answer becomes
          a wrong one. Under the cap the two are equal and this reads as a plain count.
        */}
        <p className="text-muted-foreground mt-1 text-xs">
          {/*
            Two different sentences, because the run was either inside its window or it was
            not, and those are different facts about the same list. Outside it, every row
            deferred: saying "would have placed" there implies a pass that already happened
            and reads as a contradiction against "122 outside calling hours" in the funnel.
          */}
          {planned.length < total
            ? `The first ${planned.length.toLocaleString()} of ${total.toLocaleString()} calls this run ${deferredUntil ? "would place" : "would have placed"}.`
            : `All ${total.toLocaleString()} call${total === 1 ? "" : "s"} this run ${deferredUntil ? "would place" : "would have placed"}.`}{" "}
          {deferredUntil
            ? `The calling window (${windowLabel}) was shut, so nothing would go out now — these are queued for the next open, freshest first. `
            : ""}
          {/*
            Without this, "All 150 calls" sits directly under "353 rows were callable" and reads
            as a contradiction — the run is correct and the screen looks broken. The cap is a
            deferral, not a rejection: the held rows are the first thing the next pass picks up.
          */}
          {held > 0
            ? `${held.toLocaleString()} further callable row${held === 1 ? "" : "s"} hit this feed's per-run cap and would wait for the next pass, not be dropped. `
            : ""}
          {/*
            Numbers in full, which is the change Arun asked for: *"can you show me the list of
            numbners that would be called so i can go through them and review and flag if its
            off?"* A masked column cannot answer the one question the list exists for, and the
            stored-records table next door has shown `phone_e164` in full for the same reason.
            Nothing here is written down — a dry run stores no records, and run history keeps
            only the masked form.
          */}
          Numbers and mapped values are shown exactly as they would be sent, and checking them
          is what this list is for. Nothing here has been stored.
        </p>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Appointment</TableHead>
              <TableHead>In the CRM</TableHead>
              <TableHead>What the agent would be told</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={`${p.recordId}-${p.occurrenceDate}`}>
                <TableCell className="align-top font-mono whitespace-nowrap">
                  {p.phone}
                </TableCell>
                <TableCell className="align-top whitespace-nowrap">
                  <span>{p.occurrenceDate}</span>
                  {p.occurredAt ? (
                    <span className="text-muted-foreground block text-xs">
                      {DateTime.fromISO(p.occurredAt).toFormat("HH:mm")}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="align-top font-mono text-xs whitespace-nowrap">
                  {p.recordId}
                </TableCell>
                {/*
                  Spelled out rather than folded into a `<details>` the way stored rows do it.
                  On a stored row the fields are history; here they are the thing being checked,
                  and a check you have to click forty times is a check nobody performs.
                */}
                <TableCell className="align-top whitespace-normal">
                  <ul className="w-[360px] min-w-[360px] space-y-0.5">
                    {Object.entries(p.fields).map(([k, v]) => (
                      <li key={k} className="flex gap-2 text-xs">
                        <span className="text-muted-foreground w-[130px] shrink-0 truncate font-mono">
                          {k}
                        </span>
                        <span className="min-w-0 font-mono break-words">
                          {v || <span className="text-muted-foreground italic">empty</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        className="-mx-4 -mb-4"
        page={page}
        limit={limit}
        total={planned.length}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n);
          setPage(0);
        }}
        limitOptions={[25, 50, 100, 250]}
      />
    </div>
  );
}

// ------------------------------------------------- which channel it dials on ---

/**
 * The feed's agent channel: what it is, and a picker to change it.
 *
 * Reads the app's channel cache, so opening the page costs nothing; "Get latest channels" is
 * the button that actually asks 8loop. Channels that cannot originate are listed and disabled
 * rather than hidden — a campaign-dialler channel looks perfectly configured, and somebody who
 * cannot find theirs in the list has a worse problem than somebody who can see why it is out.
 */
function CrmChannelSection({
  customer,
  label,
}: {
  customer: string;
  label: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const channels = useOrchestratorChannels(customer, open);
  const refresh = useOrchestratorRefreshChannels(customer);
  const set = useOrchestratorSetChannel(customer);
  const data = channels.data;
  const chosen = data?.chosen ?? null;

  const current = chosen
    ? (chosen.channel_label ?? chosen.routing_identifier)
    : (data?.from_config ?? null);

  return (
    <SettingsSection
      title="Which channel does this feed call out on?"
      description={
        data?.env_override && data.channels.length === 0
          ? "Set by an environment variable on this deployment, which overrides anything chosen here."
          : "Each feed dials on its own agent channel. Pick one here and it applies from the next run; clear it and the feed goes back to the channel in its config file."
      }
    >
      {!open ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm">
            {chosen ? (
              <>
                <span className="font-medium">{chosen.channel_label ?? "Chosen here"}</span>{" "}
                <span className="text-muted-foreground font-mono text-xs">
                  {chosen.routing_identifier}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">
                Using the channel in this feed's config file. Open the picker to change it.
              </span>
            )}
          </p>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {chosen ? "Change channel" : "Choose a channel"}
          </Button>
        </div>
      ) : channels.isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : channels.isError ? (
        <p className="text-destructive text-sm">
          Could not list this account's channels ({errorMessage(channels.error)}).
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={chosen?.routing_identifier ?? ""}
              onValueChange={(v) =>
                set.mutate(
                  { routing_identifier: v },
                  {
                    onSuccess: (r) => {
                      toast.success(r.message);
                      setOpen(false);
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  },
                )
              }
            >
              <SelectTrigger className="w-[440px]">
                <SelectValue placeholder="Pick an agent channel" />
              </SelectTrigger>
              <SelectContent>
                {(data?.channels ?? [])
                  .filter((c) => c.routing_identifier)
                  .map((c) => (
                    <SelectItem
                      key={c.channel_id}
                      value={c.routing_identifier!}
                      disabled={!c.dialable}
                    >
                      {c.label}
                      {c.agent_name ? ` · ${c.agent_name}` : ""}
                      {c.dialable ? "" : ` · cannot dial${c.blocked_reason ? ` (${c.blocked_reason})` : ""}`}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={refresh.isPending}
              onClick={() =>
                refresh.mutate(undefined, {
                  onSuccess: (r) =>
                    toast.success(
                      r.refresh_error
                        ? `Showing the cached list — 8loop said: ${r.refresh_error}`
                        : `Read ${r.channels.length} channel(s) from 8loop.`,
                    ),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              <RefreshCw className={cn("size-3.5", refresh.isPending && "animate-spin")} />
              Get latest channels
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            {data?.channels.length ?? 0} channel(s) on this account
            {data?.channels.some((c) => !c.dialable)
              ? ", including some 8loop will not originate on — those are disabled here, because picking one produces a feed that fails on every row."
              : "."}
            {data?.from_config ? (
              <>
                {" "}
                Its config file says{" "}
                <span className="font-mono">{data.from_config}</span>, which is what clearing goes
                back to.
              </>
            ) : null}
          </p>
          {chosen ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={set.isPending}
              onClick={() =>
                set.mutate(
                  { clear: true },
                  {
                    onSuccess: (r) => {
                      toast.success(r.message);
                      setOpen(false);
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  },
                )
              }
            >
              Use the config file's channel instead
            </Button>
          ) : null}
        </div>
      )}
      {current ? (
        <p className="text-muted-foreground text-xs">
          {label} dials on <span className="font-mono">{current}</span>.
        </p>
      ) : null}
    </SettingsSection>
  );
}
